import { FuneralStatementSection, MonarchStatementDraft } from "../types";

interface PdfDownloadResult {
  ok: boolean;
  reason?: string;
}

function cleanPdfText(value: string) {
  return value
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, "\"")
    .replace(/[\u2013\u2014]/g, "-")
    .replace(/[^\x20-\x7E]/g, " ")
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)");
}

function wrapText(value: string, maxLength = 86) {
  const words = value.split(/\s+/).filter(Boolean);
  const lines: string[] = [];
  let current = "";

  words.forEach((word) => {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length > maxLength && current) {
      lines.push(current);
      current = word;
    } else {
      current = candidate;
    }
  });

  if (current) lines.push(current);
  return lines;
}

function sectionLines(section: FuneralStatementSection) {
  return [
    `${section.title} - ${section.totalLabel}`,
    ...section.lineItems.map(
      (item) =>
        `  ${item.label}: ${item.amountLabel}${item.needsReview ? " (needs review)" : ""}`,
    ),
  ];
}

function buildStatementLines(draft: MonarchStatementDraft) {
  const sections = [
    draft.serviceCharges,
    draft.merchandiseCharges,
    draft.specialCharges,
    draft.cashAdvanceItems,
  ];

  return [
    "WEHONOR / MONARCH FUNERAL HOME",
    "DRAFT - Statement of Funeral Goods and Services Selected",
    "",
    `Loved one: ${draft.lovedOneName}`,
    `Date of passing: ${draft.dateOfPassingLabel}`,
    `Statement date: ${draft.dateOfStatementLabel}`,
    `Purchaser: ${draft.purchaserName}`,
    `Phone: ${draft.purchaserPhone}`,
    `Email: ${draft.purchaserEmail}`,
    "",
    ...sections.flatMap((section) => [...sectionLines(section), ""]),
    "Missing fields",
    ...draft.missingFields.map((field) => `  - ${field}`),
    "",
    "Disclosures and acknowledgement",
    ...draft.disclosures.flatMap((line) => wrapText(line, 92).map((wrapped) => `  ${wrapped}`)),
    "  Draft only. A licensed funeral director must review pricing, availability, and disclosures.",
    "",
    "Purchaser signature: ________________________________ Date: ____________",
    "Director review: ____________________________________ Date: ____________",
    "",
    "Review footer: Karen review required before family delivery.",
  ];
}

export function buildMonarchStatementPdfContent(draft: MonarchStatementDraft) {
  const pageWidth = 612;
  const pageHeight = 792;
  const marginLeft = 54;
  let y = 742;
  const pages: string[][] = [[]];

  const currentPage = () => pages[pages.length - 1]!;

  const addPageStamp = (commands: string[], pageNumber: number) => {
    commands.push("BT /F2 18 Tf 472 742 Td (DRAFT) Tj ET");
    commands.push(
      `BT /F1 8 Tf 54 34 Td (Generated by WeHonor Arrangement Studio. Page ${pageNumber} of ${pages.length}) Tj ET`,
    );
  };

  const startNewPage = () => {
    pages.push([]);
    y = 742;
  };

  const addLine = (line: string, options?: { bold?: boolean; size?: number; x?: number }) => {
    const size = options?.size ?? 10;
    if (y < 62 + size + 6) {
      startNewPage();
    }
    const font = options?.bold ? "/F2" : "/F1";
    const x = options?.x ?? marginLeft;
    currentPage().push(`BT ${font} ${size} Tf ${x} ${y} Td (${cleanPdfText(line)}) Tj ET`);
    y -= size + 6;
  };

  const addBlankLine = () => {
    if (y < 70) {
      startNewPage();
      return;
    }
    y -= 8;
  };

  buildStatementLines(draft).forEach((line, index) => {
    if (index === 0) {
      addLine(line, { bold: true, size: 13 });
      return;
    }
    if (index === 1) {
      addLine(line, { bold: true, size: 16 });
      addBlankLine();
      return;
    }
    if (!line) {
      addBlankLine();
      return;
    }
    wrapText(line, 92).forEach((wrappedLine) => {
      const sectionHeading =
        wrappedLine === "Missing fields" || wrappedLine === "Disclosures and acknowledgement";
      addLine(wrappedLine, {
        bold: sectionHeading || /^[A-D]\. /.test(wrappedLine),
        size: sectionHeading ? 12 : 10,
      });
    });
  });

  pages.forEach((commands, index) => addPageStamp(commands, index + 1));

  const pageObjectIds = pages.map((_, index) => 3 + index * 2);
  const contentObjectIds = pages.map((_, index) => 4 + index * 2);
  const fontRegularObjectId = 3 + pages.length * 2;
  const fontBoldObjectId = 4 + pages.length * 2;
  const objects = [
    "<< /Type /Catalog /Pages 2 0 R >>",
    `<< /Type /Pages /Kids [${pageObjectIds.map((id) => `${id} 0 R`).join(" ")}] /Count ${pages.length} >>`,
  ];

  pages.forEach((commands, index) => {
    const contentStream = commands.join("\n");
    objects.push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidth} ${pageHeight}] /Contents ${contentObjectIds[index]} 0 R /Resources << /Font << /F1 ${fontRegularObjectId} 0 R /F2 ${fontBoldObjectId} 0 R >> >> >>`,
      `<< /Length ${contentStream.length} >>\nstream\n${contentStream}\nendstream`,
    );
  });

  objects.push(
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica >>",
    "<< /Type /Font /Subtype /Type1 /BaseFont /Helvetica-Bold >>",
  );

  let pdf = "%PDF-1.4\n";
  const offsets: number[] = [];

  objects.forEach((object, index) => {
    offsets.push(pdf.length);
    pdf += `${index + 1} 0 obj\n${object}\nendobj\n`;
  });

  const xrefOffset = pdf.length;
  pdf += `xref\n0 ${objects.length + 1}\n`;
  pdf += "0000000000 65535 f \n";
  offsets.forEach((offset) => {
    pdf += `${String(offset).padStart(10, "0")} 00000 n \n`;
  });
  pdf += `trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`;
  pdf += `startxref\n${xrefOffset}\n%%EOF\n`;

  return pdf;
}

export function downloadMonarchStatementPdf(draft: MonarchStatementDraft): PdfDownloadResult {
  if (
    typeof document === "undefined" ||
    typeof Blob === "undefined" ||
    typeof URL === "undefined"
  ) {
    return {
      ok: false,
      reason: "PDF download is available from the web app.",
    };
  }

  const filename = `${draft.lovedOneName.toLowerCase().replace(/[^a-z0-9]+/g, "-")}-statement-draft.pdf`;
  const blob = new Blob([buildMonarchStatementPdfContent(draft)], {
    type: "application/pdf",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);

  return { ok: true };
}
